type BrowserCallback = (...args: Array<unknown>) => void;
interface DeviceInfo {
    touch: boolean;
    mobile: boolean;
    reducedMotion: boolean;
}

// While typeScript doesn't support navigation.connection interface yet, we define it here as mock.
interface NetworkInformation extends EventTarget {
    readonly type?: string;
    readonly effectiveType?: string;
    readonly downlinkMax?: number;
    readonly downlink?: number;
    readonly rtt?: number;
    readonly saveData?: boolean;
    onchange?: EventListener;
}

/**
 * @memberof SharedComponents
 * @class Browser
 * @alias browser
 */
class Browser {
    mobileMedia!: MediaQueryList;
    tabletMedia!: MediaQueryList;
    desktopMedia!: MediaQueryList;
    hoverCapableMedia!: MediaQueryList;

    private readonly hoverCapableChangeListeners!: Array<BrowserCallback>;
    private removeHoverCapableChangeHandler!: (() => void) | null;
    private deviceInfo!: DeviceInfo;

    static readonly MediaBreakpoint: Record<string, string> = {
        Mobile: '(max-width: 767px)',
        Tablet: '(min-width: 768px) and (max-width: 1023.98px)',
        Desktop: '(min-width: 1024px)',
    };

    constructor() {
        this.mobileMedia = window.matchMedia(Browser.MediaBreakpoint.Mobile);
        this.tabletMedia = window.matchMedia(Browser.MediaBreakpoint.Tablet);
        this.desktopMedia = window.matchMedia(Browser.MediaBreakpoint.Desktop);
        this.hoverCapableMedia = window.matchMedia(
            '(any-hover: hover) and (any-pointer: fine)'
        );
        this.hoverCapableChangeListeners = [];
        this.deviceInfo = this.getDeviceInfo();
        window.addEventListener('resize', this.handleViewportResize.bind(this));
    }

    /**
     * Checks if the browser support touch
     * @returns {Record<string, boolean>}
     */
    getDeviceInfo(): DeviceInfo {
        return {
            touch: this.supportTouch(),
            mobile: this.isMobile(),
            reducedMotion: this.prefersReducedMotion(),
        };
    }

    /**
     * Checks if the browser support touch
     * @returns {boolean}
     */
    supportTouch(): boolean {
        return Boolean(
            'ontouchstart' in window ||
                'onmsgesturechange' in window ||
                navigator.maxTouchPoints
        );
    }

    /**
     * Check if user has slow or cellular connection
     * Reference for spec:
     * http://wicg.github.io/netinfo/
     * @returns {boolean}
     * @deprecated
     */
    isSlowOrCellularConnection(): boolean {
        if (!('connection' in navigator)) {
            return false;
        }
        const connection = navigator.connection as NetworkInformation;
        if (connection) {
            if ('cellular' == connection.type) {
                return true;
            }

            // effectiveType:  'slow-2g', '2g', '3g', or '4g'.
            if (
                ['slow-2g', '2g', '3g'].includes(
                    String(connection.effectiveType)
                )
            ) {
                return false;
            }
        }
        return false;
    }

    /**
     * Checks if the browser is mobile
     * @returns {boolean}
     */
    isMobile(): boolean {
        const toMatch = [
            /Android/i,
            /webOS/i,
            /iPhone/i,
            /iPad/i,
            /iPod/i,
            /BlackBerry/i,
            /Windows Phone/i,
        ];
        return toMatch.some((toMatchItem) =>
            navigator.userAgent.match(toMatchItem)
        );
    }

    /**
     * Is desktop screen width
     * @returns {boolean}
     */
    desktopMediaMatches(): boolean {
        return this.desktopMedia.matches;
    }

    /**
     * Is tablet screen width
     * @returns {boolean}
     */
    tabletMediaMatches(): boolean {
        return this.tabletMedia.matches;
    }

    /**
     * Is mobile screen width
     * @returns {boolean}
     */
    mobileMediaMatches(): boolean {
        return this.mobileMedia.matches;
    }

    /**
     * Is device hover capable
     * @returns {boolean}
     */
    hoverCapableMediaMatches(): boolean {
        return this.hoverCapableMedia.matches;
    }

    /**
     * Creates a targeted media query change handler and returns cleanup
     * function for removing the created handler.
     * @param {MediaQueryList} mediaQuery
     * @param {Array<(function(): void)>} callbacks
     * @returns {(function(): void)}
     */
    addMediaQueryChangeHandler = (
        mediaQuery: MediaQueryList,
        callbacks: Array<BrowserCallback>
    ): BrowserCallback => {
        const mediaQueryChangeHandler = () => {
            callbacks.forEach((callback) => {
                callback();
            });
        };

        mediaQuery.addEventListener('change', mediaQueryChangeHandler);
        return () => {
            mediaQuery.removeEventListener('change', mediaQueryChangeHandler);
        };
    };

    /**
     * Adds hover capability change callback and creates
     * the event listener if not already present.
     * @param {(function(): void)} callback
     */
    addHoverCapableChangeListener(callback: BrowserCallback) {
        this.hoverCapableChangeListeners.push(callback);

        this.removeHoverCapableChangeHandler ??=
            this.addMediaQueryChangeHandler(
                this.hoverCapableMedia,
                this.hoverCapableChangeListeners
            );
    }

    /**
     * Removes hover capability change callback and if no more
     * left, removes the event listener entirely.
     * @param {(function(): void)} callback
     */
    removeHoverCapableChangeListener(callback: BrowserCallback) {
        this.hoverCapableChangeListeners.splice(
            this.hoverCapableChangeListeners.indexOf(callback),
            1
        );

        if (this.hoverCapableChangeListeners.length === 0) {
            this.removeHoverCapableChangeHandler?.();
            this.removeHoverCapableChangeHandler = null;
        }
    }

    /**
     * Checks if the user prefers reduced motion,
     * could be used to simplify transitions and animations
     * @returns {boolean}
     */
    prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /**
     * Fires callback when window resized
     * @private
     * @fires event:browser:update
     */
    handleViewportResize() {
        const currentData = this.getDeviceInfo();

        const isChanged =
            this.deviceInfo.touch !== currentData.touch ||
            this.deviceInfo.mobile !== currentData.mobile ||
            this.deviceInfo.reducedMotion !== currentData.reducedMotion;

        if (!isChanged) {
            return;
        }

        this.deviceInfo = this.getDeviceInfo();

        const event = new CustomEvent('browser:update', {
            bubbles: true,
            detail: currentData,
        });
        document.body.dispatchEvent(event);
    }

    /**
     * Checks that browser supports IntersectionObserver.
     * @returns {boolean}
     * @deprecated
     */
    supportsIntersectionObserver(): boolean {
        return (
            'IntersectionObserver' in window &&
            'IntersectionObserverEntry' in window &&
            'intersectionRatio' in window.IntersectionObserverEntry.prototype
        );
    }

    /**
     * Resolves first level domain from full domain name.
     * @returns {string}
     */
    resolveMainDomain(): string {
        const hostname = window.location.hostname;
        // check for IP address
        // ^ Start of the line
        // (?:[0-9]{1,3}\.){3}[0-9]{1,3} Matches the 4 groups of
        // [0-9]{1, 3} digits from 1 to 3
        // \. separated with dot
        // $ End of the line
        // Examples: 1.1.1.1, 22.22.22.22, 1.22.333.444, etc.
        if (/^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.exec(hostname)) {
            return hostname;
        }

        const match = /[^.]*\.[^.]{2,3}(?:\.[^.])?$/.exec(hostname);
        return String(match ?? hostname);
    }
}

export const browser = new Browser();

/**
 * @memberof Browser
 * @event browser:update
 * @alias event:browser:update
 * @augments {CustomEvent}
 */
